import { Eye, Loader2, Save, Send } from 'lucide-react';
import { Button } from '@/components/atoms/button';

interface EstimateFormActionsProps {
  isEditMode: boolean;
  /** Any submit flow is in flight — disables every button to block double-submits. */
  submitting: boolean;
  /** Only the Save / Save as Draft button is the one spinning. */
  savePending: boolean;
  /** Only the Save & Send button is the one spinning. */
  sendPending: boolean;
  canSave: boolean;
  /** Why saving is blocked, so a greyed-out button isn't a dead end. */
  blockedReason?: string | null;
  /** Recovery for transient membership-pricing lookup failures. */
  onRetryBlocked?: () => void;
  onCancel: () => void;
  onSave: () => void;
  onSaveAndSend: () => void;
  onPreview?: () => void;
}

export function EstimateFormActions({
  isEditMode,
  submitting,
  savePending,
  sendPending,
  canSave,
  blockedReason,
  onRetryBlocked,
  onCancel,
  onSave,
  onSaveAndSend,
  onPreview,
}: EstimateFormActionsProps) {
  const disabled = submitting || !canSave;

  return (
    <>
      {/* A greyed-out Save has to say why. Truncated to survive the toolbar; the
          full text stays available as the title and to screen readers.

          The region is always mounted so assistive tech is already observing it
          when a reason appears — a role="status" node inserted at the same
          moment its text arrives is not reliably announced. */}
      <p
        id="estimate-save-blocked-reason"
        role="status"
        className={
          blockedReason
            ? 'max-w-[10rem] truncate text-xs text-destructive sm:max-w-[22rem]'
            : 'sr-only'
        }
        title={blockedReason ?? undefined}
      >
        {blockedReason}
      </p>
      {/* Cancel is redundant with the back arrow on mobile */}
      <Button variant="outline" size="sm" className="hidden sm:inline-flex" onClick={onCancel}>
        Cancel
      </Button>
      {onPreview && (
        <Button variant="outline" size="sm" onClick={onPreview}>
          <Eye className="mr-1.5 h-3.5 w-3.5" />
          Preview
        </Button>
      )}
      {onRetryBlocked && (
        <Button variant="outline" size="sm" onClick={onRetryBlocked}>
          Retry pricing
        </Button>
      )}
      <Button
        size="sm"
        disabled={disabled}
        aria-describedby={blockedReason ? 'estimate-save-blocked-reason' : undefined}
        onClick={onSave}
      >
        {savePending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : !isEditMode ? (
          <Save className="mr-1.5 h-3.5 w-3.5" />
        ) : null}
        {isEditMode ? 'Save' : 'Save as Draft'}
      </Button>
      {!isEditMode && (
        <Button
          size="sm"
          aria-label="Save and send estimate"
          disabled={disabled}
          aria-describedby={blockedReason ? 'estimate-save-blocked-reason' : undefined}
          onClick={onSaveAndSend}
        >
          {sendPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4 sm:mr-2" />
          )}
          <span className="hidden sm:inline">Save &amp; Send</span>
        </Button>
      )}
    </>
  );
}
